import { onMounted, onUnmounted, toValue, watch, type MaybeRefOrGetter } from 'vue'

const STYLE_ID = 'thorne-og-select-fix'
const BOUND_ATTR = 'data-thorne-og-bound'

function getInnerSelect(freq: Element) {
  return (freq as HTMLElement).shadowRoot
    ?.querySelector('og-select')
    ?.shadowRoot?.querySelector('select')
}

function injectStyles(freq: Element) {
  const freqRoot = (freq as HTMLElement).shadowRoot
  const ogSelect = freqRoot?.querySelector('og-select')
  const innerRoot = ogSelect?.shadowRoot
  if (!innerRoot || innerRoot.getElementById(STYLE_ID)) return

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    span {
      display: none !important;
      visibility: hidden !important;
    }
    select {
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  `
  innerRoot.appendChild(style)

  if (ogSelect && freqRoot && !freqRoot.getElementById(`${STYLE_ID}-outer`)) {
    const outerStyle = document.createElement('style')
    outerStyle.id = `${STYLE_ID}-outer`
    outerStyle.textContent = `
      og-select {
        display: block;
        width: 100%;
        height: 100%;
      }
    `
    freqRoot.appendChild(outerStyle)
  }
}

function openSelect(freq: Element) {
  const select = getInnerSelect(freq) as HTMLSelectElement | null
  if (!select) return

  select.focus()

  if (typeof select.showPicker === 'function') {
    try {
      select.showPicker()
      return
    } catch {
      // fall through
    }
  }

  select.click()
}

function bindClick(freq: Element) {
  if (freq.hasAttribute(BOUND_ATTR)) return
  freq.setAttribute(BOUND_ATTR, 'true')

  freq.addEventListener('click', (e) => {
    const select = getInnerSelect(freq)
    if (!select) return

    const rect = (freq as HTMLElement).getBoundingClientRect()
    const arrowZoneWidth = 44
    const clickX = (e as MouseEvent).clientX - rect.left

    if (clickX >= rect.width - arrowZoneWidth) {
      e.preventDefault()
      openSelect(freq)
    }
  })
}

function initOrderGrooveSelect() {
  document.querySelectorAll('og-select-frequency').forEach((freq) => {
    injectStyles(freq)
    bindClick(freq)
  })
}

export function useOrderGrooveSelectFix(isEnabled: MaybeRefOrGetter<boolean> = true) {
  if (!import.meta.client) return

  let observer: MutationObserver | null = null
  let stopWatch: (() => void) | null = null

  const start = () => {
    if (observer || !document.body) return

    initOrderGrooveSelect()
    observer = new MutationObserver(initOrderGrooveSelect)
    observer.observe(document.body, { childList: true, subtree: true })
  }

  const stop = () => {
    observer?.disconnect()
    observer = null
  }

  onMounted(() => {
    stopWatch = watch(
      () => toValue(isEnabled),
      (enabled) => {
        if (enabled) {
          start()
        } else {
          stop()
        }
      },
      { immediate: true }
    )
  })

  onUnmounted(() => {
    stopWatch?.()
    stop()
  })
}
